import type { AddProductPayload, Cart, CartApiResponse, CartSummary } from '~/types/cart'
import { URL_CART } from '~/stores/cart'
import BaseProxyController from '~/service/base'
import { assertIsDefined } from '~/utils/assert'
import type { StampinupError } from '~/utils/http/api-error'
import { addCommonQueryParam } from '~/utils/general'
import { flagCartFailure } from '~/utils/cart-failure'
import type {$Fetch} from "nitropack";
export interface CartUpdateResult {
  cartSummaryOverview: CartSummary
  cart: Cart
  errors?: StampinupError[]
}


export abstract class CartServiceBase {
  BASE_URL = 'cart'
  COUPON_URL = URL_CART.COUPONCODE as string
  api: $Fetch

  constructor() {
    const { $api } = useNuxtApp()
    this.api = $api
  }


  getProductUrlByItemType (productId?: string) {
    let endpointUrl = this.BASE_URL
    if (productId) {
      endpointUrl += `/${productId}`
    }
    return endpointUrl
  }

  /**
   * Every cart call goes through here so a 5xx/404 raises the cart failure dialog.
   * This replaces Nuxt2's `@HandleCartFailure()` decorator, which cannot compile
   * under the Nuxt4 tsconfig — see `~/utils/cart-failure`. AB#63254.
   */
  protected async request<T> (url: string, options?: Parameters<$Fetch>[1]): Promise<T> {
    try {
      return await this.api<T>(url, options)
    } catch (error) {
      flagCartFailure(error)
      throw error
    }
  }

  async fetch () {
    const query = addCommonQueryParam()
    // `apiError` is destructured defensively throughout: the type declares it required, but
    // that is an assertion about the contract rather than a runtime guarantee, and this
    // codebase does not itself trust it — stores/cart.ts reads `data.apiError?.errors` on the
    // same cart endpoints. An absent envelope would otherwise throw on a SUCCESSFUL call.
    // AB#63254
    const { cart, cartSummaryOverview: cartSummary, apiError } =
        await this.request<CartApiResponse>(this.BASE_URL, { query })
    return { cart, cartSummaryOverview: cartSummary, errors: apiError?.errors }
  }

  async addProduct ({ product, quantity }: AddProductPayload): Promise<CartUpdateResult | undefined> {
    const endpointUrl = this.getProductUrlByItemType()
    const productId = product.id || product.productId
    assertIsDefined(productId, 'for adding product productId should be defined in cart service')
    const query = addCommonQueryParam()
    const { cart, cartSummaryOverview: cartSummary, apiError } = await this.request<CartApiResponse>(endpointUrl, {
      body: {
        productId,
        quantity
      },
      query,
      method: 'POST'
    })
    return { cart, cartSummaryOverview: cartSummary, errors: apiError?.errors }
  }

  async updateQuantity ({ product, quantity, isPromotionalPricing }: AddProductPayload): Promise<CartUpdateResult | undefined> {
    const productId = product.id || product.productId
    assertIsDefined(productId, 'for updating product quantity productId should be defined in cart service')
    const endpointUrl = `${this.getProductUrlByItemType(productId)}?quantity=${quantity}`
    const query = addCommonQueryParam()
    const { cart, cartSummaryOverview: cartSummary, apiError } = await this.request<CartApiResponse>(endpointUrl, {
      body: {
        productId,
        quantity,
        isPromotionalPricing
      },
      query,
      method: 'PUT'
    })
    return { cart, cartSummaryOverview: cartSummary, errors: apiError?.errors }
  }

  async removeProduct ({ productId, isPromotionalPricing }: { productId: string, isPromotionalPricing?: boolean }) {
    const endpointUrl = `${this.getProductUrlByItemType(productId)}`
    const query = addCommonQueryParam()
    if (isPromotionalPricing) {
      query.isPromotionalPricing = isPromotionalPricing.toString()
    }
    const { cart, cartSummaryOverview: cartSummary, apiError } = await this.request<CartApiResponse>(endpointUrl, {
      query,
      method: 'DELETE'
    })
    return { cart, cartSummaryOverview: cartSummary, errors: apiError?.errors }
  }

  // These two intentionally return the response body untyped. Annotating them `Promise<void>`
  // was tried and reverted under AB#63254: the body IS part of the contract (cart.spec.ts
  // asserts it), and naming a concrete type here would invent a shape nothing verifies — the
  // service mock is cast, so a wrong annotation would typecheck happily. Left as-is until the
  // endpoint's real response shape is pinned down.
  async addCouponCode (couponCode: string) {
    const url = `${this.BASE_URL}${this.COUPON_URL}${couponCode}`
    const query = addCommonQueryParam()
    return await this.request(url, {
      query,
      method: 'PUT'
    })
  }

  async removeCouponCode (couponCode: string) {
    const url = `${this.BASE_URL}${this.COUPON_URL}${couponCode}`
    const query = addCommonQueryParam()
    return await this.request(url, {
      query,
      method: 'DELETE'
    })
  }
}

export class UserCartService extends CartServiceBase {
}
/* istanbul ignore next */
export default () => {
  const checkoutProxyController = new BaseProxyController({
    UserService: UserCartService
  })
  const proxy: CartServiceBase = new Proxy<UserCartService>({} as unknown as UserCartService, checkoutProxyController)
  return proxy
}
